import { PVC_SIGN_ROUTE_PATH } from "../../../../packages/pvc-product/pvc-sign-route";
import {
  PRODUCT_CATALOG_PATH,
  productPagePath,
} from "../product-pages/product-pages-content";
import { WORK_GALLERY_PATH } from "../work-gallery/work-gallery-api";
import { CONTACT_PATH } from "../public-routes";

/**
 * The one and only customer-facing brand name. Never shorten it to
 * "רק בשמחות" and never surface "HappyPrint" as the storefront brand —
 * HappyPrint remains an internal/system name only.
 */
export const BUSINESS_NAME = "בית דפוס רק בשמחות";
export const BUSINESS_NAME_LINE_1 = "בית דפוס";
export const BUSINESS_NAME_LINE_2 = "רק בשמחות";
export const BUSINESS_TAGLINE = "מדפיסים את הרגעים שלכם";

export type NavItem = { id: string; label: string; href: string };

export const NAV_ITEMS: NavItem[] = [
  { id: "nav-products", label: "מוצרים", href: PRODUCT_CATALOG_PATH },
  { id: "nav-signs", label: "שילוט לעסקים", href: PVC_SIGN_ROUTE_PATH },
  { id: "nav-events", label: "אירועים ושמחות", href: "/#category-events" },
  { id: "nav-business-print", label: "דפוס עסקי", href: "/#category-business" },
  { id: "nav-gifts", label: "מתנות ממותגות", href: "/#category-gifts" },
  { id: "nav-gallery", label: "העבודות שלנו", href: WORK_GALLERY_PATH },
  { id: "nav-contact", label: "צור קשר", href: CONTACT_PATH },
];

export type CategoryVisual = "sign" | "rollup" | "stickers" | "event" | "business" | "gift";

/**
 * Featured products intentionally render with a distinct, more dimensional
 * "product mockup" art style (wall-mounted, shadowed, bolted) rather than
 * reusing the flatter category-icon artwork — so the two sections don't look
 * like the same illustration recolored.
 */
export type MockupVisual = "sign-mockup" | "rollup-mockup" | "stickers-mockup" | "event-mockup" | "business-mockup" | "gift-mockup";

export type Category = {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  icon: "signs" | "rollups" | "stickers" | "events" | "business" | "gifts";
  visual: CategoryVisual;
};

export const CATEGORIES: Category[] = [
  {
    id: "category-signs",
    title: "שלטים לפי מידה",
    subtitle: "שלטים, PVC, קאפה, שמשוניות ועוד",
    href: `${PVC_SIGN_ROUTE_PATH}`,
    icon: "signs",
    visual: "sign",
  },
  {
    id: "category-rollups",
    title: "רולאפים ותצוגה",
    subtitle: "רולאפים, מתקני תצוגה ושילוט",
    href: productPagePath("rollup"),
    icon: "rollups",
    visual: "rollup",
  },
  {
    id: "category-stickers",
    title: "מדבקות",
    subtitle: "מדבקות בכל מידה וצורה",
    href: productPagePath("stickers-roll"),
    icon: "stickers",
    visual: "stickers",
  },
  {
    id: "category-events",
    title: "אירועים ושמחות",
    subtitle: "קירות צילום, הזמנות ומיתוג לאירועים",
    href: productPagePath("event-invitations"),
    icon: "events",
    visual: "event",
  },
  {
    id: "category-business",
    title: "דפוס עסקי",
    subtitle: "כרטיסי ביקור, פליירים ומוצרי משרד",
    href: productPagePath("business-cards"),
    icon: "business",
    visual: "business",
  },
  {
    id: "category-gifts",
    title: "מתנות ממותגות",
    subtitle: "מיתוג אישי לעסק ולאירוע",
    href: productPagePath("printed-tshirts"),
    icon: "gifts",
    visual: "gift",
  },
];

export type TrustBarItem = { id: string; label: string; icon: "truck" | "quality" | "design" | "custom" };

export const TRUST_BAR_ITEMS: TrustBarItem[] = [
  { id: "trust-shipping", label: "איסוף עצמי מקריית ביאליק", icon: "truck" },
  { id: "trust-quality", label: "איכות הדפסה גבוהה", icon: "quality" },
  { id: "trust-design", label: "שירות גרפי מקצועי", icon: "design" },
  { id: "trust-custom", label: "הזמנה בהתאמה אישית", icon: "custom" },
];

/**
 * A slim decorative strip above the header, reusing the same approved
 * trust-bar copy (no new claims). Purely visual reinforcement — hidden from
 * assistive tech in SiteHeader.tsx since the same content is announced
 * properly by the real TrustBar section further down the page.
 */
export const TOPBAR_MESSAGES: string[] = TRUST_BAR_ITEMS.map(item => item.label);

/**
 * The homepage only renders a price when it has an exact, authoritative
 * configuration quote. PVC has a fixed homepage configuration (60×40, qty 1),
 * so it can safely show a live server quote here. Other cards link to their
 * full product configurator, where server pricing is calculated from the
 * customer's actual selections rather than from a native variant list price.
 */
export type FeaturedProduct = {
  id: string;
  name: string;
  href: string;
  ctaLabel: string;
  visual: MockupVisual;
};

export const FEATURED_PRODUCTS: FeaturedProduct[] = [
  { id: "product-pvc-sign", name: "שלט PVC לפי מידה", href: PVC_SIGN_ROUTE_PATH, ctaLabel: "בחירת מידה והזמנה", visual: "sign-mockup" },
  { id: "product-business-cards", name: "כרטיסי ביקור", href: productPagePath("business-cards"), ctaLabel: "לפרטים", visual: "business-mockup" },
  { id: "product-rollup", name: "רולאפ", href: productPagePath("rollup"), ctaLabel: "לקבלת מחיר", visual: "rollup-mockup" },
  { id: "product-stickers-roll", name: "מדבקות בגליל", href: productPagePath("stickers-roll"), ctaLabel: "לפרטים", visual: "stickers-mockup" },
  { id: "product-event-invites", name: "הזמנות לאירוע", href: productPagePath("event-invitations"), ctaLabel: "לפרטים", visual: "event-mockup" },
  { id: "product-tshirts", name: "חולצות מודפסות", href: productPagePath("printed-tshirts"), ctaLabel: "לקבלת מחיר", visual: "gift-mockup" },
];

export type AudienceBanner = {
  id: string;
  audience: "business" | "event";
  title: string;
  copy: string;
  ctaLabel: string;
  href: string;
};

export const AUDIENCE_BANNERS: AudienceBanner[] = [
  {
    id: "banner-business",
    audience: "business",
    title: "צריכים שילוט לעסק?",
    copy: "שלטים, מדבקות, רולאפים, מיתוג ועוד לעסקים בכל גודל",
    ctaLabel: "למוצרי עסקים",
    href: productPagePath("business-cards"),
  },
  {
    id: "banner-event",
    audience: "event",
    title: "מחפשים משהו לאירוע?",
    copy: "הזמנות, שלטים, מדבקות, קירות צילום ומיתוג לאירועים ושמחות",
    ctaLabel: "למוצרי אירועים",
    href: productPagePath("event-invitations"),
  },
];

export type HowItWorksStep = { step: number; label: string; icon: "pick" | "size" | "upload" | "approve" | "receive" };

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  { step: 1, label: "בוחרים מוצר", icon: "pick" },
  { step: 2, label: "מגדירים מידה וכמות", icon: "size" },
  { step: 3, label: "מעצבים או מעלים קובץ", icon: "upload" },
  { step: 4, label: "בודקים ומזמינים", icon: "approve" },
  { step: 5, label: "מקבלים את ההזמנה", icon: "receive" },
];

export type BenefitItem = { id: string; icon: "service" | "quality" | "clock" | "truck"; title: string; description: string };

export const BENEFIT_ITEMS: BenefitItem[] = [
  {
    id: "benefit-service",
    icon: "service",
    title: "שירות אישי",
    description: "ליווי צמוד מהבחירה ועד לקבלת המוצר, עם מענה אנושי לכל שאלה.",
  },
  {
    id: "benefit-quality",
    icon: "quality",
    title: "איכות הדפסה גבוהה",
    description: "חומרי גלם איכותיים והדפסה מדויקת שנשארת חדה לאורך זמן.",
  },
  {
    id: "benefit-timing",
    icon: "clock",
    title: "זמני הכנה ברורים",
    description: "מועד ההכנה המשוער מוצג בתמחור. בהזמנות מיוחדות מתאמים איתנו מראש.",
  },
  {
    id: "benefit-shipping",
    icon: "truck",
    title: "איסוף עצמי מקריית ביאליק",
    description: "איסוף עצמי בתיאום מראש. אפשרויות אספקה נוספות מוצגות בהזמנה כשהן זמינות.",
  },
];

export const FOOTER_PRODUCT_LINKS: NavItem[] = [
  { id: "footer-products", label: "מוצרים", href: PRODUCT_CATALOG_PATH },
  { id: "footer-signs", label: "שילוט לעסקים", href: PVC_SIGN_ROUTE_PATH },
  { id: "footer-events", label: "אירועים ושמחות", href: productPagePath("event-invitations") },
  { id: "footer-business-print", label: "דפוס עסקי", href: productPagePath("business-cards") },
  { id: "footer-stickers", label: "מדבקות", href: productPagePath("stickers-roll") },
  { id: "footer-rollups", label: "רולאפים ושמשוניות", href: productPagePath("rollup") },
];

/**
 * Account and order tracking are live routes. Unsupported legal/info pages
 * remain intentionally absent from public navigation until real content exists.
 */

